import React, { useMemo } from 'react'
import { useFitStore } from '../store/useFitStore'
import { useNavigate } from 'react-router-dom'
import Button from '../components/shared/Button'
import Card from '../components/shared/Card'
import { expandOccurrences } from '../lib/ics'

/* ============================================================
   Today — the coach's daily digest. Answers one question the
   moment the day starts: "what needs me right now?"
   - today's appointments (recurrence-aware, via the .ics expander)
   - check-ins due this week · lead follow-ups due
   - this week's programming progress
   Everything links to the surface where the work happens
   (Planner / Calendar / Leads / Training Log).
============================================================ */

const isoLocal = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const mondayOf = (d: Date): string => {
  const x = new Date(d);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return isoLocal(x);
};
const fmtTime = (d: Date) => d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

// Monochrome status scale — brightness encodes severity (urgent = bright, on-track = dim)
const statusShade: Record<string, string> = {
  red: 'rgba(255,255,255,0.85)',
  yellow: 'rgba(255,255,255,0.45)',
  green: 'rgba(255,255,255,0.18)',
};

const CoachDashboard: React.FC = () => {
  const { clients, leads, appointments, sessions, checkIns } = useFitStore();
  const navigate = useNavigate();

  const now = new Date();
  const todayIso = isoLocal(now);
  const weekStart = mondayOf(now);
  const weekEndDate = new Date(now);
  weekEndDate.setDate(weekEndDate.getDate() + (6 - ((now.getDay() + 6) % 7)));
  const weekEnd = isoLocal(weekEndDate);

  // Today's appointments, recurrence-aware (same expansion the .ics feed uses).
  const todaysAppts = useMemo(() => {
    const dayStart = new Date(now); dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(now); dayEnd.setHours(23, 59, 59, 999);
    return appointments
      .filter((a) => a.status !== 'canceled')
      .flatMap((a) => expandOccurrences(a, dayStart, dayEnd).map((occ) => ({ appt: a, occ })))
      .sort((x, y) => x.occ.start.getTime() - y.occ.start.getTime());
  }, [appointments, todayIso]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check-ins due = roster minus everyone already checked in this week.
  const checkedIn = useMemo(
    () => new Set(checkIns.filter((c) => c.weekOf === weekStart).map((c) => c.clientId)),
    [checkIns, weekStart]
  );
  const checkInsDue = clients.filter((c) => !checkedIn.has(c.id));

  // Lead follow-ups due today or overdue, still open.
  const followUpsDue = leads.filter(
    (l) => l.followUpAt && l.followUpAt <= todayIso && l.status !== 'won' && l.status !== 'lost'
  );

  // This week's programming progress across the whole roster.
  const week = useMemo(() => {
    const mine = sessions.filter((s) => s.date >= weekStart && s.date <= weekEnd);
    return { total: mine.length, done: mine.filter((s) => s.status === 'completed').length };
  }, [sessions, weekStart, weekEnd]);

  const attentionCount = todaysAppts.length + checkInsDue.length + followUpsDue.length;

  const dateLabel = now.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });

  const digest: { value: string; label: string; to: string }[] = [
    { value: String(todaysAppts.length), label: 'Appointments today', to: '/coach/calendar' },
    { value: String(checkInsDue.length), label: 'Check-ins due', to: '/coach' },
    { value: String(followUpsDue.length), label: 'Follow-ups due', to: '/coach/leads' },
    { value: week.total ? `${week.done}/${week.total}` : '—', label: 'Sessions this week', to: '/coach' },
  ];

  return (
    <div className="main-content">
      <header className="mb-8">
        <p className="mono-label">{dateLabel}</p>
        <h1 className="hero text-white text-[48px] leading-[0.95] font-bold tracking-[-0.03em] mt-1 mb-5">Today.</h1>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" onClick={() => navigate('/coach')}>Open Planner →</Button>
          <Button variant="glass" size="sm" onClick={() => navigate('/coach/calendar')}>New appointment</Button>
          <Button variant="glass" size="sm" onClick={() => navigate('/coach/program/new')}>New program</Button>
        </div>
      </header>

      {/* the day at a glance */}
      <div data-tour="digest" className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        {digest.map((d) => (
          <Card key={d.label} className="p-4 card-interactive" onClick={() => navigate(d.to)}>
            <p className="text-3xl font-bold tabular-nums">{d.value}</p>
            <p className="text-fit-muted text-[10px] uppercase tracking-widest mt-1">{d.label}</p>
          </Card>
        ))}
      </div>

      {/* needs attention — appointments first (time-ordered), then due items */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold text-fit-text mb-3">Needs attention</h2>
        {attentionCount === 0 ? (
          <Card className="p-5">
            <p className="text-[14px] font-semibold">Nothing needs you right now.</p>
            <p className="text-[12px] text-fit-muted mt-0.5">No appointments, check-ins, or follow-ups due today.</p>
          </Card>
        ) : (
          <div className="flex flex-col gap-2">
            {todaysAppts.map(({ appt, occ }) => (
              <Card key={`${appt.id}-${occ.start.getTime()}`} className="p-4 card-interactive" onClick={() => navigate('/coach/calendar')}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[14px] font-semibold truncate">
                      {appt.title}
                      {appt.attendeeName && <span className="text-fit-text-secondary font-normal"> — {appt.attendeeName}</span>}
                    </p>
                    <p className="text-[12px] text-fit-muted">{fmtTime(occ.start)} – {fmtTime(occ.end)}{appt.location && ` · ${appt.location}`}</p>
                  </div>
                  <span className="mono-label shrink-0">calendar →</span>
                </div>
              </Card>
            ))}
            {checkInsDue.map((c) => (
              <Card key={c.id} className="p-4 card-interactive" onClick={() => navigate(`/coach?client=${c.id}&hub=1`)}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[14px] font-semibold truncate">Check-in due — {c.name}</p>
                    <p className="text-[12px] text-fit-muted">No check-in logged for the week of {weekStart.slice(5)}</p>
                  </div>
                  <span className="mono-label shrink-0">open hub →</span>
                </div>
              </Card>
            ))}
            {followUpsDue.map((l) => (
              <Card key={l.id} className="p-4 card-interactive" onClick={() => navigate('/coach/leads')}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[14px] font-semibold truncate">Follow up — {l.name}</p>
                    <p className="text-[12px] text-fit-muted">due {l.followUpAt}{l.source && ` · ${l.source}`}</p>
                  </div>
                  <span className="mono-label shrink-0">leads →</span>
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* roster — name, triage, straight to the work */}
      <section>
        <h2 className="text-lg font-semibold text-fit-text mb-3">Clients</h2>
        <div className="flex flex-col gap-2">
          {clients.map((client) => (
            <div
              key={client.id}
              className="flex items-center justify-between gap-3 p-4 bg-fit-bg-secondary border border-fit-line rounded-fit-md border-l-4 text-white"
              style={{ borderLeftColor: statusShade[client.status] }}
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="w-9 h-9 rounded-full bg-fit-bg-tertiary flex items-center justify-center text-[12px] font-bold shrink-0">
                  {client.name.split(' ').map((w) => w[0]).slice(0, 2).join('')}
                </span>
                <div className="min-w-0">
                  <h3 className="text-[15px] font-bold tracking-tight truncate">{client.name}</h3>
                  <p className="text-fit-muted text-xs capitalize">{client.lifecycle || 'active'}{checkedIn.has(client.id) ? ' · checked in ✓' : ''}</p>
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button variant="glass" size="sm" onClick={() => navigate(`/coach?client=${client.id}&hub=1`)}>Manage</Button>
                <Button variant="glass" size="sm" onClick={() => navigate(`/coach/client/${client.id}/log`)}>Log</Button>
              </div>
            </div>
          ))}
          {clients.length === 0 && (
            <Card className="p-5">
              <p className="text-[14px] font-semibold">No clients yet.</p>
              <p className="text-[12px] text-fit-muted mt-0.5 mb-3">Type them in or import a spreadsheet — takes a minute.</p>
              <Button size="sm" onClick={() => navigate('/coach?import=1')}>Add clients →</Button>
            </Card>
          )}
        </div>
      </section>
    </div>
  )
}

export default CoachDashboard
